// BeforeAfterAlbum.js
// Final Version: Exact Match for "History Chips" Design 🎨
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BeforeAfterUpload from './BeforeAfterUpload'; 
import ComparisonExportModal from './ComparisonExportModal'; 

const BeforeAfterAlbum = () => {
    // 1. State Management
    const [patientData, setPatientData] = useState(null);
    const [beforePhotos, setBeforePhotos] = useState(Array(30).fill(null)); 
    const [afterPhotos, setAfterPhotos] = useState(Array(30).fill(null));   
    const [loading, setLoading] = useState(true);
    const [showExport, setShowExport] = useState(false);
    const [error, setError] = useState(null);
    
    // 📅 DATE MANAGEMENT
    const urlParams = new URLSearchParams(window.location.search);
    const initialDate = urlParams.get('date') || new Date().toISOString().split('T')[0];
    const [currentDate, setCurrentDate] = useState(initialDate);
    const [availableDates, setAvailableDates] = useState([]); // List of dates from DB

    // 👤 PATIENT PARAMETERS (same as main app)
    const branchHN = urlParams.get('branch_hn') || urlParams.get('patient');
    const patientName = urlParams.get('name');
    const branch = urlParams.get('branch');
    const branchCode = urlParams.get('branch_code');
    const doctor = urlParams.get('doctor');
    const centralHN = urlParams.get('hn') || urlParams.get('central_hn');

    // UPLOAD MODAL STATE
    const [showUpload, setShowUpload] = useState(false);
    const [uploadConfig, setUploadConfig] = useState({ category: 'before', index: 0 });

    console.log('🧪 BeforeAfterAlbum Debug:');
    console.log('📍 URL:', window.location.href);
    console.log('🏥 Branch HN:', branchHN);
    console.log('👤 Patient Name:', patientName);
    console.log('🏢 Branch:', branch);
    console.log('🏷️ Branch Code:', branchCode);
    console.log('👨‍⚕️ Doctor:', doctor);
    console.log('🆔 Central HN:', centralHN);
    console.log('📅 Date:', currentDate);

    const patientContext = {
        branchHN: branchHN,
        name: patientData?.name || patientName || 'Loading...',
        visitDate: currentDate,
        doctor: patientData?.doctor_name || doctor || 'Staff',
        branch: branch,
        branchCode: branchCode,
        centralHN: centralHN
    };

    // 1. Initial Load
    useEffect(() => {
        if (branchHN) {
            fetchPatientData();
            fetchAvailableDates(); // 🔍 Load History
            setError(null);
        } else {
            setError('ข้อมูลผู้ป่วยไม่ครบถ้วน - Missing Branch HN');
            setLoading(false);
        }
    }, [branchHN]);

    // 2. Fetch Photos whenever Date Changes
    useEffect(() => {
        if (branchHN && currentDate) {
            fetchBeforeAfterPhotos(currentDate);
            const newUrl = `${window.location.pathname}?branch_hn=${branchHN}&date=${currentDate}`;
            window.history.replaceState(null, '', newUrl);
        }
    }, [currentDate, branchHN]);

    const fetchAvailableDates = async () => {
        try {
            const response = await axios.get(`/api/patient/${branchHN}/before-after-dates`);
            if (response.data.success) {
                setAvailableDates(response.data.dates || []);
            }
        } catch (err) {
            console.error('Failed to load history dates', err);
        }
    };

    const fetchPatientData = async () => {
        try {
            const response = await axios.get(`/api/patient/${branchHN}/info`);
            if (response.data.success) setPatientData(response.data.patient);
        } catch (error) {
            setError('ไม่พบข้อมูลผู้ป่วย');
        }
    };

    const fetchBeforeAfterPhotos = async (dateToFetch) => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/patient/${branchHN}/before-after/${dateToFetch}`);
            
            if (response.data.success) {
                const { before_photos, after_photos } = response.data;
                const beforeGrid = Array(30).fill(null);
                const afterGrid = Array(30).fill(null);
                
                before_photos.forEach(p => { if (p.slot_index < 30) beforeGrid[p.slot_index] = p; });
                after_photos.forEach(p => { if (p.slot_index < 30) afterGrid[p.slot_index] = p; });
                
                setBeforePhotos(beforeGrid);
                setAfterPhotos(afterGrid);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadClick = (category, slotIndex) => {
        setUploadConfig({ category, index: slotIndex });
        setShowUpload(true);
    };

    const handleUploadSuccess = (result) => {
        console.log('✅ Upload success:', result);
        setShowUpload(false);

        const uploadedPhotos = Array.isArray(result.photos) ? result.photos.flat() : [];
        const beforeCopy = [...beforePhotos];
        const afterCopy = [...afterPhotos];

        uploadedPhotos.forEach(photo => {
            if (photo && typeof photo.slot_index === 'number') {
                if (result.category === 'before') {
                    if (photo.slot_index < 30) beforeCopy[photo.slot_index] = photo;
                } else {
                    if (photo.slot_index < 30) afterCopy[photo.slot_index] = photo;
                }
            }
        });

        if (result.category === 'before') {
            setBeforePhotos(beforeCopy);
        } else {
            setAfterPhotos(afterCopy);
        }

        fetchBeforeAfterPhotos(currentDate);
        fetchAvailableDates(); // Refresh chips
    };

    const handleDeletePhoto = async (category, slotIndex) => {
        const photoData = category === 'before' ? beforePhotos[slotIndex] : afterPhotos[slotIndex];
        if (!photoData) return;
        if (!window.confirm(`ต้องการลบรูปหรือไม่?`)) return;
        
        try {
            await axios.post(`/api/patient/${branchHN}/visit/${photoData.id}/soft-delete`, {
                reason: 'Deleted from Album', deletedBy: 'Staff'
            });
            if (category === 'before') {
                const newGrid = [...beforePhotos]; newGrid[slotIndex] = null; setBeforePhotos(newGrid);
            } else {
                const newGrid = [...afterPhotos]; newGrid[slotIndex] = null; setAfterPhotos(newGrid);
            }
            setTimeout(fetchAvailableDates, 1000);
        } catch (e) { alert('ลบไม่สำเร็จ'); }
    };

    const handlePhotoClick = (c, i) => {
        const p = c === 'before' ? beforePhotos[i] : afterPhotos[i];
        if (p) window.open(`/api/patient/${branchHN}/before-after-photo/${p.filename}`, '_blank');
    };

    // Get angle label for slot
    const getAngleLabel = (slotIndex) => {
        const position = slotIndex % 3;
        const angleMap = { 0: 'L (ซ้าย)', 1: 'C (กลาง)', 2: 'R (ขวา)' };
        return angleMap[position] || 'L';
    };

    // RENDER SLOT FUNCTION
    const renderPhotoSlot = (category, slotIndex) => {
        const photoData = category === 'before' ? beforePhotos[slotIndex] : afterPhotos[slotIndex];
        const hasPhoto = !!photoData;
        const isFirstRow = slotIndex < 3;
        const slotLabel = `ช่องที่ ${slotIndex + 1}`;
        const angleLabel = getAngleLabel(slotIndex);
        const themeColor = category === 'before' ? '#2196f3' : '#e91e63'; 
        const lightBg = category === 'before' ? '#e3f2fd' : '#fce4ec';
        
        return (
            <div
                key={`${category}-${slotIndex}`}
                style={{
                    width: '80px', position: 'relative'
                }}
            >
                <div
                    style={{
                        width: '80px', height: '140px', border: '2px dashed #ccc', borderRadius: '6px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: hasPhoto ? '#f8f9fa' : '#ffffff', position: 'relative', cursor: 'pointer',
                        borderColor: isFirstRow ? themeColor : '#ccc', borderWidth: isFirstRow ? '3px' : '2px'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = themeColor; e.currentTarget.style.backgroundColor = hasPhoto ? lightBg : '#f8f9fa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = isFirstRow ? themeColor : '#ccc'; e.currentTarget.style.backgroundColor = hasPhoto ? '#f8f9fa' : '#ffffff'; }}
                >
                    {hasPhoto ? (
                        <>
                            <img 
                                src={photoData.url || photoData.preview} 
                                onClick={() => handlePhotoClick(category, slotIndex)} 
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',   // 🚀 Keeps it cropped nicely
                                    objectPosition: 'center center', // 🎯 Ensures faces aren't chopped off
                                    imageOrientation: 'from-image', // 🎯 Safety net for older iOS versions
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    display: 'block'
                                }} 
                                alt={`${category} slot ${slotIndex}`}
                                onError={(e) => { 
                                    console.error(`Failed to load image: ${photoData.url}`); 
                                    e.target.style.display = 'none'; 
                                }}
                            />
                            <button onClick={(e) => { e.stopPropagation(); handleDeletePhoto(category, slotIndex); }} style={{ position: 'absolute', top: '2px', right: '2px', width: '22px', height: '22px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>🗑️</button>
                        </>
                    ) : (
                        <div onClick={() => handleUploadClick(category, slotIndex)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#6c757d', padding: '4px' }}>
                            <div style={{ fontSize: '20px', color: themeColor }}>+</div>
                            <div style={{ fontSize: '8px', textAlign: 'center', lineHeight: '1.2' }}>{slotIndex + 1}</div>
                        </div>
                    )}
                </div>
                {/* Angle Label */}
                <div style={{
                    position: 'absolute', bottom: '-18px', left: '50%', transform: 'translateX(-50%)',
                    fontSize: '9px', fontWeight: 'bold', color: '#888', backgroundColor: '#f0f0f0',
                    padding: '1px 6px', borderRadius: '3px', whiteSpace: 'nowrap'
                }}>
                    {angleLabel}
                </div>
            </div>
        );
    };

    if (loading && !patientData) return <div style={{ textAlign: 'center', padding: '50px' }}>⏳ Loading...</div>;
    if (error) return <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>❌ {error}</div>;

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* HEADER */}
            <div style={{
                background: 'linear-gradient(135deg, #fce4ec 0%, #f3e5f5 100%)', // Pink gradient
                padding: '25px', borderRadius: '16px', marginBottom: '30px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.08)', 
                borderLeft: '8px solid #d81b60', 
            }}>
                <h1 style={{ margin: '0 0 20px 0', color: '#2c3e50', fontSize: '28px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    📸 อัลบั้มก่อน-หลัง <span style={{fontSize: '18px', color: '#888', fontWeight: 'normal'}}>(Before-After Album)</span>
                </h1>
                
                <div style={{ display: 'flex', gap: '30px', fontSize: '15px', color: '#555', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div><strong>👤 ผู้ป่วย:</strong> {patientData?.name || patientName} ({branchHN})</div>
                    <div><strong>🏥 สาขา:</strong> {patientData?.branch_name || branch || 'Loading...'}</div>
                </div>

                {/* 📅 SMART DATE BAR (White Box with Chips) */}
                <div style={{ 
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '15px',
                    backgroundColor: 'white', padding: '15px', borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #eee'
                }}>
                    {/* 1. Date Picker */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '24px' }}>🗓️</span>
                        <div>
                            <label style={{ fontSize: '12px', color: '#888', display: 'block', fontWeight: 'bold' }}>เลือกวันที่:</label>
                            <input 
                                type="date"
                                value={currentDate} 
                                onChange={(e) => setCurrentDate(e.target.value)}
                                style={{ 
                                    border: '1px solid #ddd', borderRadius: '6px', padding: '5px 10px',
                                    fontSize: '14px', color: '#007bff', fontWeight: 'bold', outline: 'none'
                                }}
                            />
                        </div>
                    </div>

                    {/* 2. Divider */}
                    <div style={{ width: '1px', height: '40px', backgroundColor: '#eee' }}></div>

                    {/* 3. History Chips */}
                    {availableDates.length > 0 ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: '#888' }}>หรือเลือกจากประวัติ:</span>
                            {availableDates.map(date => {
                                const isSelected = date === currentDate;
                                return (
                                    <button
                                        key={date}
                                        onClick={() => setCurrentDate(date)}
                                        style={{
                                            padding: '6px 14px',
                                            borderRadius: '20px',
                                            border: isSelected ? '1px solid #e91e63' : '1px solid #ddd',
                                            backgroundColor: isSelected ? '#e91e63' : '#fff',
                                            color: isSelected ? 'white' : '#666',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                            boxShadow: isSelected ? '0 2px 5px rgba(233, 30, 99, 0.3)' : 'none'
                                        }}
                                    >
                                        {new Date(date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                                    </button>
                                );
                            })}
                            {/* 🚀 NEW: Add Export Button here for quick access */}
                            <button 
                                onClick={() => setShowExport(true)} 
                                style={{ padding: '8px 20px', backgroundColor: '#673ab7', color: 'white', border: 'none', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}
                            >
                                🖨️ ทำใบสรุป
                            </button>
                        </div>
                    ) : (
                        <div style={{ color: '#aaa', fontSize: '14px', fontStyle: 'italic' }}>
                            (ยังไม่มีประวัติอัลบั้มเก่า)
                        </div>
                    
                    )}                    
                </div>
            </div>

            {/* SUMMARY BOXES */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: '#e3f2fd', color: '#1565c0', padding: '15px 25px', borderRadius: '12px', border: '1px solid #90caf9', textAlign: 'center', minWidth: '150px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{beforePhotos.filter(p => p).length}</div>
                    <div style={{ fontSize: '14px' }}>📷 รูปก่อน (Before)</div>
                </div>
                <div style={{ backgroundColor: '#fce4ec', color: '#c2185b', padding: '15px 25px', borderRadius: '12px', border: '1px solid #f48fb1', textAlign: 'center', minWidth: '150px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{afterPhotos.filter(p => p).length}</div>
                    <div style={{ fontSize: '14px' }}>📷 รูปหลัง / ติดตามผล (After / Follow-up)</div>
                </div>
            </div>

            {/* MAIN GRID */}
            <div style={{ display: 'flex', gap: '50px', justifyContent: 'center', backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <div style={{ flex: 1, maxWidth: '500px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ textAlign: 'center', flex: 1, color: '#2196f3', borderBottom: '3px solid #2196f3', paddingBottom: '12px', margin: 0 }}>❄️ ก่อนการรักษา (BEFORE)</h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', paddingBottom: '35px' }}>
                        {beforePhotos.map((_, i) => renderPhotoSlot('before', i))}
                    </div>
                </div>
                <div style={{ width: '2px', backgroundColor: '#eee' }}></div>
                <div style={{ flex: 1, maxWidth: '500px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ textAlign: 'center', flex: 1, color: '#e91e63', borderBottom: '3px solid #e91e63', paddingBottom: '12px', margin: 0 }}>🌸 หลังการรักษา (AFTER)</h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', paddingBottom: '35px' }}>
                        {afterPhotos.map((_, i) => renderPhotoSlot('after', i))}
                    </div>
                </div>
            </div>

            {/* FOOTER */}
            <div style={{ marginTop: '40px', textAlign: 'center', padding: '20px', display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <button onClick={() => handleUploadClick('before', 0)} style={{ padding: '12px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>📤 Bulk Upload Before</button>
                <button onClick={() => handleUploadClick('after', 0)} style={{ padding: '12px 20px', backgroundColor: '#FF9800', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>📤 Bulk Upload After</button>
                <button onClick={() => window.close()} style={{ padding: '12px 30px', backgroundColor: '#607d8b', color: 'white', border: 'none', borderRadius: '6px' }}>✖️ ปิดหน้าต่าง</button>
                <button onClick={() => setShowExport(true)} style={{ padding: '12px 30px', backgroundColor: '#673ab7', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>🖨️ สร้างใบสรุป (Print PDF)</button>
                <button onClick={() => fetchBeforeAfterPhotos(currentDate)} style={{ padding: '12px 30px', backgroundColor: '#00bcd4', color: 'white', border: 'none', borderRadius: '6px' }}>🔄 รีเฟรช</button>
            </div>

            {showUpload && <BeforeAfterUpload isOpen={showUpload} onClose={() => setShowUpload(false)} patientContext={patientContext} onUploadSuccess={handleUploadSuccess} initialCategory={uploadConfig.category} initialSlotIndex={uploadConfig.index} />}
            {showExport && (
                <ComparisonExportModal 
                    isOpen={showExport} 
                    onClose={() => setShowExport(false)}
                    beforePhotos={beforePhotos}
                    afterPhotos={afterPhotos}
                    patientContext={patientContext}
                />
            )}
        </div>
    );
};

export default BeforeAfterAlbum;